import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useFormContext, useWatch } from 'react-hook-form';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import {
    ConsolidatedLocationView,
    DispatchAreaOption,
    EventPanelFormType,
    EventPanelMetadata,
} from '@mark43/cad-api';
import { useGetAgencyType } from '../common/hooks/use-get-agency-type';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { isValidLatLng } from '../location/helpers';
import { getClosestLocation } from '../common/helpers/get-closest-location';
import { EditablePersonFormProperties, EditableVehicleFormProperties } from './types';
import { getDefaultVehicle } from '../event-panel/event-panel-form/involved-person-vehicle-fields/vehicles/event-panel-vehicle-helpers';
import { getDefaultPerson } from '../event-panel/event-panel-form/involved-person-vehicle-fields/persons/helpers';
import { useLocationApi } from '../location-select/hooks/use-location-api';
import { useAgencyTypeGlobalAttributeId } from '../common/hooks/use-agency-type-global-attribute-id';
import { useEventFormCallForServiceOptions } from '../common/hooks/use-event-form-call-for-service-options';
import { useEventMetadataStore } from '../event-panel/event-panel-form/event-metadata';
import { useUnitLocationStore } from '../fr-unit-location/use-unit-location-store';
import { useStreamUnitLocation } from '../common/hooks/use-stream-unit-location';
import { GeoPointLocation } from '../esri/types';
import { getEventSubdivisionFieldsParams } from '../common/helpers/get-event-subdivision-fields-params';
import { mappedSubdivisionAttrIdsLabels, validateAndDirtyFormField } from './constants';
import {
    useAgencyPrefill,
    useEventOriginPrefill,
    usePriorityPrefill,
    useRiskLevelPrefill,
} from '../common/hooks/use-form-prefill-utils';
import { prefillAttributeFieldBasedOnId } from '../common/helpers/form-prefill';

export function useHandleReverseGeocodeClosestLocation({
    isAutomaticPrefill,
}: {
    isAutomaticPrefill: boolean;
}) {
    const { lat: unitGpsLatitude, lng: unitGpsLongitude } =
        useUnitLocationStore()?.unitGpsInfo || {};
    const { frSelfInitiateGpsLocation: frSelfInitiateGpsLocationEnabled } = useEnabledFeatures();
    const form = useFormContext<EventPanelFormType>();
    const prefillLocationInfo = usePrefillLocationInfo();
    const [isPrefilledWithUnitGpsLocation, setIsPrefilledWithUnitGpsLocation] = useState(false);
    const [closestLocationForManualPrefill, setClosestLocationForManualPrefill] =
        useState<ConsolidatedLocationView>();
    const { eventId } = useEventMetadataStore()?.state?.metadata ?? ({} as EventPanelMetadata);
    const isEventCreation = !eventId;

    const {
        searchLocations,
        resolveEntityLinksForLocation,
        isLoadingResults,
        isClassifyingLocation,
    } = useLocationApi();

    const manuallyPrefillFormLocationWithClosestLocation = useCallback(() => {
        if (!closestLocationForManualPrefill) {
            return;
        }

        form.setValue('location', closestLocationForManualPrefill, validateAndDirtyFormField);
        setIsPrefilledWithUnitGpsLocation(true);
        prefillLocationInfo(closestLocationForManualPrefill);
    }, [
        closestLocationForManualPrefill,
        form,
        prefillLocationInfo,
        setIsPrefilledWithUnitGpsLocation,
    ]);

    useEffect(
        () => {
            if (isEventCreation) {
                form.setValue('location', {}, validateAndDirtyFormField);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isEventCreation]
    );

    useEffect(
        () => {
            if (
                !frSelfInitiateGpsLocationEnabled ||
                !isValidLatLng(unitGpsLatitude, unitGpsLongitude)
            ) {
                return;
            }

            searchLocations(`${unitGpsLatitude},${unitGpsLongitude}`)
                .then((results) => {
                    const closestLocation = getClosestLocation(results);

                    if (closestLocation) {
                        resolveEntityLinksForLocation(closestLocation)
                            .then((classifiedClosestLocation: ConsolidatedLocationView) => {
                                setClosestLocationForManualPrefill(classifiedClosestLocation);

                                if (isAutomaticPrefill) {
                                    form.setValue(
                                        'location',
                                        classifiedClosestLocation,
                                        validateAndDirtyFormField
                                    );
                                    prefillLocationInfo(classifiedClosestLocation);
                                }
                            })
                            .catch(() => {
                                setClosestLocationForManualPrefill(closestLocation);

                                if (isAutomaticPrefill) {
                                    form.setValue(
                                        'location',
                                        closestLocation,
                                        validateAndDirtyFormField
                                    );
                                    prefillLocationInfo(closestLocation);
                                }
                            })
                            .finally(() => {
                                setIsPrefilledWithUnitGpsLocation(isAutomaticPrefill);
                            });
                    }
                })
                .catch(() => {
                    setClosestLocationForManualPrefill(undefined);
                    setIsPrefilledWithUnitGpsLocation(false);
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [unitGpsLatitude, unitGpsLongitude]
    );

    return {
        isLoading: isLoadingResults || isClassifyingLocation,
        closestLocationForManualPrefill,
        manuallyPrefillFormLocationWithClosestLocation,
        isPrefilledWithUnitGpsLocation,
        setIsPrefilledWithUnitGpsLocation,
    };
}

export function usePrefillRadioChannelBasedOnDispatchArea() {
    const agencyTypeGlobalAttrId = useAgencyTypeGlobalAttributeId();
    const form = useFormContext<EventPanelFormType>();
    const { eventPanelOptions: eventOptions } = useBootstrapState();
    const getAgencyType = useGetAgencyType(agencyTypeGlobalAttrId);
    const { radioChannels: radioChannelsEnabled } = useEnabledFeatures();

    return useCallback(
        (newDispatchArea: DispatchAreaOption) => {
            if (!radioChannelsEnabled || !newDispatchArea?.radioChannelId) {
                return;
            }

            const newRadioChannelOption = eventOptions?.radioChannelOptions.find(
                (radioChannelOption) => radioChannelOption.value === newDispatchArea.radioChannelId
            );

            if (newRadioChannelOption) {
                form.setValue(`${getAgencyType()}PrimaryRadioChannel`, newRadioChannelOption);
            }
        },
        [eventOptions?.radioChannelOptions, getAgencyType, form, radioChannelsEnabled]
    );
}

function usePrefillDispatchAreaBasedOnLocation() {
    const agencyTypeGlobalAttrId = useAgencyTypeGlobalAttributeId();
    const form = useFormContext<EventPanelFormType>();
    const getAgencyType = useGetAgencyType(agencyTypeGlobalAttrId);
    const { eventPanelOptions: eventOptions } = useBootstrapState();
    const prefillRadioChannelBasedOnDispatchArea = usePrefillRadioChannelBasedOnDispatchArea();

    return useCallback(
        (newLocation: ConsolidatedLocationView) => {
            if (!newLocation?.areCoordinatesVerified) {
                return;
            }

            const newDispatchArea = newLocation?.entityLinks?.[0]?.dispatchAreas.find(
                (dispatchArea) => dispatchArea.agencyTypeGlobalAttrId === agencyTypeGlobalAttrId
            );

            if (!newDispatchArea) {
                return;
            }

            const newDispatchAreaOption = eventOptions?.[
                `${getAgencyType()}DispatchAreaOptions`
            ].find((dispatchAreaOption) => dispatchAreaOption.value === newDispatchArea.id);

            if (!newDispatchAreaOption) {
                return;
            }

            form.setValue(`${getAgencyType()}DispatchArea`, newDispatchAreaOption);
            prefillRadioChannelBasedOnDispatchArea(newDispatchAreaOption);
        },
        [
            agencyTypeGlobalAttrId,
            form,
            getAgencyType,
            prefillRadioChannelBasedOnDispatchArea,
            eventOptions,
        ]
    );
}

function usePrefillSubdivisionsBasedOnLocation() {
    const agencyTypeGlobalAttrId = useAgencyTypeGlobalAttributeId();
    const form = useFormContext<EventPanelFormType>();
    const getAgencyType = useGetAgencyType(agencyTypeGlobalAttrId);
    const { subdivisionOptionsBootstrapResponse } = useBootstrapState();
    const { multiAgencySubdivisions } = useEnabledFeatures();

    const agencyValue = useWatch({
        control: form.control,
        name: `${getAgencyType()}Agency`,
    });

    const subdivisionsFieldsParams = useMemo(
        () =>
            getEventSubdivisionFieldsParams({
                eventClassPrefix: getAgencyType(),
                agencyValue,
                multiAgencySubdivisions,
                allSubdivisionOptions:
                    subdivisionOptionsBootstrapResponse?.combinedSubdivisionOptionsBootstrapView,
                agencySubdivisionOptions:
                    subdivisionOptionsBootstrapResponse?.agencyIdToSubdivisionOptionsBootstrapView,
            }) ?? [],
        [getAgencyType, multiAgencySubdivisions, subdivisionOptionsBootstrapResponse, agencyValue]
    );

    return useCallback(
        (newLocation: ConsolidatedLocationView) => {
            if (!newLocation?.areCoordinatesVerified) {
                return;
            }

            const entityLink = newLocation?.entityLinks?.[0];

            if (!entityLink) {
                return;
            }

            for (const subdivisionFieldParams of subdivisionsFieldsParams) {
                const { name, options } = subdivisionFieldParams;

                const subdivisionAttrIdLabel = mappedSubdivisionAttrIdsLabels[name];
                const subdivisionAttrId = entityLink[subdivisionAttrIdLabel];
                const subdivisionOption = options.find(
                    (option) => option.value === subdivisionAttrId
                );

                if (!subdivisionOption) {
                    continue;
                }

                form.setValue(name, subdivisionOption);
            }
        },
        [subdivisionsFieldsParams, form]
    );
}

export function usePrefillLocationInfo() {
    const prefillDispatchAreaBasedOnLocation = usePrefillDispatchAreaBasedOnLocation();
    const prefillSubdivisionsBasedOnLocation = usePrefillSubdivisionsBasedOnLocation();

    return useCallback(
        (newLocation: ConsolidatedLocationView) => {
            prefillDispatchAreaBasedOnLocation(newLocation);
            prefillSubdivisionsBasedOnLocation(newLocation);
        },
        [prefillDispatchAreaBasedOnLocation, prefillSubdivisionsBasedOnLocation]
    );
}

export function useAddVehicleToForm() {
    const form = useFormContext<EventPanelFormType>();
    const { eventPanelConfiguration } = useBootstrapState();

    return useCallback(
        (vehicleType: EditableVehicleFormProperties) => {
            const currentList = form.getValues()?.[vehicleType];

            form.setValue(vehicleType, [
                ...currentList,
                getDefaultVehicle({
                    eventPanelConfiguration,
                    towedFromLocation:
                        vehicleType === 'towVehicles'
                            ? (form.getValues().location as ConsolidatedLocationView | undefined)
                            : undefined,
                }),
            ]);
        },
        [eventPanelConfiguration, form]
    );
}

export function useAddPersonToForm() {
    const form = useFormContext<EventPanelFormType>();
    const { eventPanelConfiguration } = useBootstrapState();

    return useCallback(
        (personType: EditablePersonFormProperties) => {
            const currentList = form.getValues()?.[personType];
            form.setValue(personType, [...currentList, getDefaultPerson(eventPanelConfiguration)]);
        },
        [eventPanelConfiguration, form]
    );
}

export function usePrefillFieldsBasedOnCallForServices() {
    const { eventPanelOptions: eventOptions } = useBootstrapState();

    const priorityOptions = eventOptions?.priorityOptions ?? [];
    const riskLevelOptions = eventOptions?.riskLevelOptions ?? [];
    const eventOriginOptions = eventOptions?.eventOriginSelfInitiateOptions ?? [];

    usePriorityPrefill(priorityOptions);
    useRiskLevelPrefill(riskLevelOptions);
    useEventOriginPrefill(eventOriginOptions);
}

export function useCreateEventFormPrefill() {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const form = useFormContext<EventPanelFormType>();
    const callForServiceOptions = useEventFormCallForServiceOptions();
    const selfInitiateCallForServiceId = Number(searchParams.get('selfInitiateCallForServiceId'));
    const eventOriginOtherOptionId = Number(searchParams.get('eventOriginOtherOptionId'));
    const { eventId } = useEventMetadataStore()?.state?.metadata ?? ({} as EventPanelMetadata);
    const isCreateEventForm = !eventId;
    const { eventPanelOptions: eventOptions } = useBootstrapState();
    const { eventsHaveRiskLevel } = useEnabledFeatures();
    const { isDirty: isCallForServiceDirty } = form.getFieldState('callForService');
    const prefillLocationInfo = usePrefillLocationInfo();
    const unitId: number | undefined = location.state?.unitId;
    const eventInitiatedFromMap: boolean | undefined = location.state?.eventInitiatedFromMap;
    const geoPointLocation: GeoPointLocation | undefined = location.state?.geoPointLocation;
    const unitGeoLocation = useStreamUnitLocation({
        unitId,
        enabled: !!eventInitiatedFromMap && !!unitId,
    });

    const [
        createEventCallForServicePrefillOptionLabel,
        setCreateEventCallForServicePrefillOptionLabel,
    ] = useState<string>();

    useEffect(
        function prefillCallForServiceAndRelatedFields() {
            if (!isCreateEventForm || isCallForServiceDirty) {
                return;
            }

            const callForServicePrefillOption = callForServiceOptions.find(
                (option) => option.value === selfInitiateCallForServiceId
            );

            if (callForServicePrefillOption) {
                setCreateEventCallForServicePrefillOptionLabel(callForServicePrefillOption.label);
                form.setValue('callForService', callForServicePrefillOption);

                prefillAttributeFieldBasedOnId({
                    prefillField: 'eventOrigin',
                    prefillOptionId: callForServicePrefillOption?.defaultForEventOriginAttrId,
                    options: eventOptions?.eventOriginSelfInitiateOptions,
                    form,
                });

                prefillAttributeFieldBasedOnId({
                    prefillField: 'priority',
                    prefillOptionId: callForServicePrefillOption?.priorityAttrId,
                    options: eventOptions?.priorityOptions,
                    form,
                });

                if (eventsHaveRiskLevel) {
                    prefillAttributeFieldBasedOnId({
                        prefillField: 'riskLevel',
                        prefillOptionId: callForServicePrefillOption?.riskLevelAttrId,
                        options: eventOptions?.riskLevelOptions,
                        form,
                    });
                }
            } else if (eventOriginOtherOptionId) {
                prefillAttributeFieldBasedOnId({
                    prefillField: 'eventOrigin',
                    prefillOptionId: eventOriginOtherOptionId,
                    options: eventOptions?.eventOriginSelfInitiateOptions,
                    form,
                });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            selfInitiateCallForServiceId,
            eventOriginOtherOptionId,
            callForServiceOptions,
            form,
            isCreateEventForm,
            eventOptions,
            eventsHaveRiskLevel,
        ]
    );

    useAgencyPrefill(isCreateEventForm);

    // `form` object may change and if used as a dependency inside a useEffect where useEffect will make an update to the form
    // an infinite re-render will occur. `setValue`, however, will not change and can be used as a dependency
    const setFormValue = form.setValue;

    useEffect(
        function prefillLocationFieldWhenEventIsCreatedFromMap() {
            if (!eventInitiatedFromMap) {
                return;
            }

            let locationPreFillData: ConsolidatedLocationView | undefined;

            if (unitGeoLocation) {
                locationPreFillData = unitGeoLocation.consolidatedLocationView;
            }
            if (geoPointLocation) {
                locationPreFillData = geoPointLocation;
            }

            if (locationPreFillData) {
                setFormValue('location', locationPreFillData, validateAndDirtyFormField);
                prefillLocationInfo(locationPreFillData);
            }
        },
        [
            eventInitiatedFromMap,
            geoPointLocation,
            prefillLocationInfo,
            unitGeoLocation,
            setFormValue,
        ]
    );

    return {
        createEventCallForServicePrefillOptionLabel,
        eventInitiatedFromMap,
    };
}
